import { Component } from '@theme/component';
import { RecentlyViewed, RECENTLY_VIEWED_UPDATED } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

class HerDadRecentlyViewed extends Component {
  #listeners;
  #request;
  #pendingKey = '';
  #renderedKey = '';

  connectedCallback() {
    super.connectedCallback();
    this.#request?.abort();
    this.#pendingKey = '';
    this.#renderedKey = '';
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    window.addEventListener(RECENTLY_VIEWED_UPDATED, this.#refresh, { signal });
    window.addEventListener('storage', this.#storageChanged, { signal });
    window.addEventListener('pageshow', this.#pageShown, { signal });
    this.#refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#request?.abort();
    this.#pendingKey = '';
    this.#renderedKey = '';
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    this.#pendingKey = '';
    this.#renderedKey = '';
  }

  #storageChanged = (event) => {
    if (event.key === 'viewedProducts' || event.key === null) this.#refresh();
  };

  #pageShown = (event) => {
    if (event.persisted && this.dataset.currentProductId) RecentlyViewed.addProduct(this.dataset.currentProductId);
    this.#refresh();
  };

  #refresh = async () => {
    const grid = this.refs.grid;
    if (!grid || !this.isConnected) return;
    const ids = RecentlyViewed.getProducts().filter((id) => id !== this.dataset.currentProductId);
    const count = Math.min(8, Math.max(2, Number(this.dataset.productCount) || 4));
    const key = `${ids.join(',')}|${count}|${this.dataset.collectionHandle || ''}`;
    if (key === this.#pendingKey) return;
    if (key === this.#renderedKey) {
      this.#request?.abort();
      this.#pendingKey = '';
      return;
    }
    this.#request?.abort();
    this.#pendingKey = '';
    if (!ids.length) {
      grid.replaceChildren();
      this.hidden = true;
      this.#renderedKey = key;
      return;
    }
    const request = new AbortController();
    this.#request = request;
    this.#pendingKey = key;
    try {
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, request.signal);
      if (request.signal.aborted || !this.isConnected) return;
      const document = new DOMParser().parseFromString(html, 'text/html');
      const template = document.querySelector('template[data-hd-recent-results]');
      if (!template) throw new Error('Recently viewed cards are unavailable');
      const cards = new Map(
        [...template.content.querySelectorAll('[data-hd-recent-product]')].map((card) => [card.dataset.hdRecentProduct, card])
      );
      const orderedCards = ids.map((id) => cards.get(id)).filter(Boolean).slice(0, count);
      if (this.dataset.collectionHandle === 'patriotic') {
        for (const card of orderedCards) {
          const alternate = card.querySelector('template[data-hd-patriotic-card]');
          if (alternate) card.replaceChildren(...alternate.content.childNodes);
        }
      }
      grid.replaceChildren(...orderedCards);
      this.hidden = orderedCards.length === 0;
      this.#renderedKey = key;
    } catch {
      if (!request.signal.aborted) {
        this.hidden = true;
        this.#renderedKey = '';
      }
    } finally {
      if (this.#request === request) this.#pendingKey = '';
    }
  };
}

if (!customElements.get('her-dad-recently-viewed')) {
  customElements.define('her-dad-recently-viewed', HerDadRecentlyViewed);
}

